import { useEffect, useRef, useState } from "react";
import StyledH3Heading from "../common/components/styledH3Heading/styledH3Heading.tsx";
import axiosInstance from "../utils/axios-instance.ts";
import moment from "moment";
import UseChat from "../utils/use-chat.ts";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/parse-errors.ts";
import UseAuth from "../utils/use-auth.ts";
import clsx from "clsx";
import RemoveHtmlTags from "../utils/remove-html-tags.ts";
import { chatStarter, flags } from "../data.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TypingAnimation from "../components/typing-animation.tsx";

export interface ISingleMessage {
  id?: number;
  conversationId: number;
  userId: number;
  message: string;
  type: "human" | "ai";
  createdAt?: string;
  updatedAt?: string;
}

interface IMessages {
  totalCount: number;
  data: ISingleMessage[];
}

const ChatWindow = () => {
  const { currentConversation, setCurrentConversation } = UseChat();
  const { user } = UseAuth();
  const [value, setValue] = useState("");
  const [messages, setMessages] = useState<IMessages>({
    totalCount: 0,
    data: [],
  });
  const lastMessageRef = useRef(null);
  const [isSending, setIsSending] = useState<boolean>(false);

  const handleSendMessage = async (message?: any) => {
    let newValue = message ? message : value;
    setIsSending(true);
    let conversationId = currentConversation?.id;
    setValue("");
    if (newValue?.trim()) {
      if (!currentConversation?.id) {
        try {
          const res = await axiosInstance.post("/chat/conversation/create", {
            message: newValue,
          });
          setCurrentConversation(res.data.data);
          conversationId = res.data.data?.id;
        } catch (error) {
          console.log(error);
          toast.error(ParseErrors(error));
          setIsSending(false);
        }
      }

      // ------------------------- [ PUSH MESSAGE ] ---------------------------------
      const newMessage: ISingleMessage = {
        message: newValue,
        conversationId: conversationId,
        userId: user?.id,
        type: "human",
        createdAt: new Date().toString(),
      };

      setMessages((prev) => ({ ...prev, data: [...prev.data, newMessage] }));

      try {
        const stream = await fetch(
          process.env.REACT_APP_API_URL + "/chat/message",
          {
            method: "POST",
            body: JSON.stringify({
              ...newMessage,
              messagesArray: messages.data.map((item) => ({
                role: item.type === "ai" ? "assistant" : "user",
                content: item?.message,
              })),
            }),
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        const streamRes = stream.body
          .pipeThrough(new TextDecoderStream())
          .getReader();
        let data = "";
        setIsSending(false);

        while (true) {
          const { done, value } = await streamRes.read();
          if (done) {
            break;
          }
          data += value;
          setMessages({
            totalCount: messages.totalCount,
            data: [
              ...messages.data,
              newMessage,
              {
                message: data,
                conversationId: conversationId,
                userId: user?.id,
                type: "ai",
              },
            ],
          });
          lastMessageRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }

        setIsSending(false);
      } catch (error) {
        console.log(error);
        setIsSending(false);
      }
    } else {
      setIsSending(false);
    }
  };

  // ------------------------ [ FIND CONVERSATION MESSAGES ] ----------------------

  useEffect(() => {
    const findMessages = async () => {
      try {
        const res = await axiosInstance.get(
          `/chat/conversation/messages/${currentConversation?.id}`
        );
        setMessages(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    currentConversation?.id && findMessages();
  }, [currentConversation?.id]);

  // ------------------------ [ UPDATE CONVERSATION ] ----------------------

  useEffect(() => {
    const updateConversations = async () => {
      try {
        const res = await axiosInstance.put(
          `/chat/conversation/${currentConversation?.id}`,
          { message: messages.data.pop().message }
        );
        setCurrentConversation(res.data.data);
      } catch (error) {
        console.log(error);
      }
    };

    currentConversation?.id &&
      messages.data.length === 3 &&
      updateConversations();
  }, [currentConversation?.id, messages.data.length]);

  // ----------------------- [ ON ENTER SEND MESSAGE ] --------------------------
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && value && !isSending) {
      handleSendMessage();
    }
  };

  // ---------------------------- [ SCROLL TO BOTTOM ] -------------------------
  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [messages?.data?.length]);

  useEffect(() => {
    if (!currentConversation?.id) {
      setMessages({ totalCount: 0, data: [] });
    }
  }, [currentConversation]);

  return (
    <div className="w-[100%] lg:w-[80%] h-full bg-white flex flex-col">
      {currentConversation?.id ? (
        <div className="w-[100%] border-b border-gray-100 bg-white/70 backdrop-blur-sm">
          <div className="w-[95%] mx-auto flex justify-between items-center py-4">
            <StyledH3Heading
              children={
                RemoveHtmlTags(
                  currentConversation?.name.replaceAll('"', "")
                ).slice(0, 90) + "..." || "Chat"
              }
              fontColor={"text-lightBlack !text-[20px] !font-semibold"}
            />
            <div className="flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100">
              <p className="text-sm font-medium text-lightBlack">English</p>
              <img src="/assets/icons/ic_chevron.png" alt="" className="w-3 opacity-60" />
            </div>
          </div>
        </div>
      ) : null}

      {!currentConversation?.id ? (
        <div className="h-full w-full items-center justify-center flex flex-col px-4 py-10">
          <div className="w-[280px] md:w-[340px] chat_display_img relative">
            <img src="/assets/images/chat-icon.jpg" className="w-full" />
          </div>
          <div className="w-full max-w-[340px] pt-2 flex gap-6 justify-center items-center">
            {flags.map((item) => {
              return (
                <div
                  key={item.imagUrl}
                  className="w-[64px] flex justify-center items-center cursor-pointer rounded-lg overflow-hidden hover:scale-105 transition-all"
                >
                  <img src={item.imagUrl} className="w-full" />
                </div>
              );
            })}
          </div>
          <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight mt-10 text-lightBlack">Conversation Starters</h2>
          <p className="text-sm text-gray-500 mt-1">Pick a prompt below or start typing your own</p>
          <div className="w-[95%] max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {chatStarter.map((item) => {
              return (
                <div
                  key={item.title}
                  className={clsx(
                    "w-full px-5 py-8 border border-gray-100 bg-white rounded-2xl cursor-pointer hover:border-blue/40 hover:shadow-card transition-all duration-200 text-center group",
                    isSending && "pointer-events-none opacity-[0.6]"
                  )}
                  onClick={() => {
                    handleSendMessage(item.message);
                  }}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gray-50 group-hover:bg-blue/10 transition-colors">
                    <FontAwesomeIcon
                      icon={item.icon}
                      size="lg"
                      color={item.iconColor}
                    />
                  </div>
                  <h2 className="mt-4 text-[14.5px] font-medium text-lightBlack leading-snug">{item.title}</h2>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="w-[100%] h-[100%] overflow-y-auto custom-scrollbar">
          {messages.data.length ? (
            <div className="w-[95%] max-w-3xl mx-auto p-4 py-8">
              {messages?.data?.map((msg, index) => (
                <div key={index} className="mb-5">
                  <div
                    className={`message_box flex ${
                      msg.type === "human" ? "justify-end" : "justify-start"
                    } mb-1`}
                  >
                    <div
                      className={`px-5 py-3.5 max-w-[85%] ${
                        msg.type === "human"
                          ? "bg-blue rounded-t-2xl rounded-bl-2xl rounded-br-md text-white shadow-soft"
                          : "bg-gray-50 rounded-t-2xl rounded-br-2xl rounded-bl-md text-lightBlack border border-gray-100"
                      }`}
                    >
                      <div
                        className="space-y-2 text-[15px] leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: msg.message }}
                      ></div>
                    </div>
                  </div>
                  <span
                    className={clsx(
                      "text-[11px] flex text-gray-400 px-1",
                      msg.type === "human" ? "justify-end" : "justify-start"
                    )}
                  >
                    {moment(msg.createdAt).format("HH:mm")}
                  </span>
                </div>
              ))}
              <div ref={lastMessageRef}></div>
              {isSending ? (
                <div className="mt-4">
                  <TypingAnimation />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      <div className="p-4 border-t border-gray-100 bg-white">
        <div className="w-full max-w-3xl mx-auto flex items-center gap-2">
          <div className="flex-grow relative">
            <input
              type="text"
              onKeyDown={handleKeyDown}
              className="w-full h-12 pl-5 pr-12 bg-gray-50 border border-gray-100 rounded-full text-[14.5px] outline-none placeholder-gray-400"
              placeholder="Start a new message"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <button
            disabled={isSending}
            onClick={() => handleSendMessage()}
            className="h-12 w-12 flex items-center justify-center rounded-full bg-blue text-white hover:bg-[#3F86C2] active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Send"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
